import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Upload, RotateCcw, Monitor, Smartphone, History, ChevronDown } from 'lucide-react';
import { adminTexts, adminTextsDraft, adminTextsPublish, adminTextsReset, adminTextsLog } from '../../services/cms';
import { useToast } from '../../hooks/use-toast';

const Field = ({ field, value, onChange }) => {
  const { key, label, type } = field;
  const common = 'w-full px-3 py-2 rounded-xl bg-muted outline-none focus:ring-2 focus:ring-primary/30';
  return (
    <div>
      <label className="text-xs font-800 uppercase text-muted-foreground block mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea value={value} onChange={(e) => onChange(key, e.target.value)} rows={3} data-testid={`field-${key}`} className={`${common} font-600 resize-y`} />
      ) : type === 'bool' ? (
        <select value={String(value).toLowerCase() === 'true' ? 'true' : 'false'} onChange={(e) => onChange(key, e.target.value)} data-testid={`field-${key}`} className={`${common} font-700 cursor-pointer`}>
          <option value="true">Afficher</option>
          <option value="false">Masquer</option>
        </select>
      ) : (
        <input value={value} onChange={(e) => onChange(key, e.target.value)} data-testid={`field-${key}`} className={`${common} font-700`} />
      )}
    </div>
  );
};

const Preview = ({ page, draft, device }) => {
  const g = (k) => draft[k] || '';
  const frame = `border border-border rounded-2xl p-5 bg-card overflow-hidden ${device === 'mobile' ? 'max-w-[360px]' : ''}`;
  return (
    <div className={frame} data-testid="texts-preview">
      {page === 'accueil' && (<>
        <p className="text-xs font-800 uppercase text-primary mb-1">{g('home_hero_surtitre')}</p>
        <h1 className="font-display font-700 text-3xl mb-2">{g('home_hero_titre')}</h1>
        <p className="text-foreground/75 font-600 mb-3">{g('home_hero_paragraphe')}</p>
        <div className="flex flex-wrap gap-2">
          <span className="px-4 py-2 rounded-full bg-primary text-white font-800 text-sm">{g('home_cta1_label')}</span>
          <span className="px-4 py-2 rounded-full bg-secondary text-secondary-foreground font-800 text-sm">{g('home_cta2_label')}</span>
        </div>
      </>)}
      {page === 'catalogue' && (<>
        <h1 className="font-display font-700 text-3xl mb-2">{g('catalogue_title')}</h1>
        <p className="text-foreground/75 font-600 mb-1">{g('catalogue_text')}</p>
        <p className="text-secondary font-800">{g('catalogue_secondary')}</p>
      </>)}
      {page === 'collections' && (<>
        <h1 className="font-display font-700 text-3xl mb-2">{g('collections_title')}</h1>
        <p className="text-foreground/75 font-600">{g('collections_intro')}</p>
      </>)}
      {page === 'auteurs' && (<>
        <h1 className="font-display font-700 text-3xl mb-2">{g('authors_title')}</h1>
        <p className="text-foreground/75 font-600">{g('authors_intro')}</p>
      </>)}
      {page === 'actualites' && (<>
        <h1 className="font-display font-700 text-3xl mb-2">{g('news_title')}</h1>
        <p className="text-foreground/75 font-600">{g('news_intro')}</p>
      </>)}
      {page === 'apropos' && (<>
        <h1 className="font-display font-700 text-3xl mb-1">{g('about_title')}</h1>
        <p className="text-primary font-800 mb-2">{g('about_subtitle')}</p>
        <p className="text-foreground/75 font-600 mb-2">{g('about_p1')}</p>
        <p className="text-foreground/75 font-600">{g('about_p2')}</p>
      </>)}
      {page === 'contact' && (<>
        <h1 className="font-display font-700 text-3xl mb-2">{g('contact_title')}</h1>
        <p className="text-foreground/75 font-600 mb-3">{g('contact_intro')}</p>
        <p className="text-sm font-700">✉️ {g('contact_email')}</p>
        <p className="text-sm font-700">📍 {g('contact_address')}</p>
        {String(g('contact_phone_enabled')).toLowerCase() === 'true' && <p className="text-sm font-700">📞 {g('contact_phone_display')}</p>}
        <span className="inline-block mt-3 px-4 py-2 rounded-full bg-primary text-white font-800 text-sm">{g('contact_submit_label')}</span>
      </>)}
    </div>
  );
};

const PublicTextsPanel = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [page, setPage] = useState('accueil');
  const [device, setDevice] = useState('desktop');
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    const d = await adminTexts();
    setData(d); setDraft({ ...d.draft });
    adminTextsLog().then(setLog).catch(() => setLog([]));
  };
  useEffect(() => { load().catch(() => { setData({ schema: [] }); setDraft({}); }); }, []);

  const schema = data?.schema || [];
  const currentPage = useMemo(() => schema.find((p) => p.page === page) || schema[0], [schema, page]);

  const upd = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const dirty = data && draft && Object.keys(data.published || {}).some((k) => (draft[k] ?? '') !== (data.published[k] ?? ''));

  const saveDraft = async () => { setSaving(true); try { await adminTextsDraft(draft); toast({ title: 'Brouillon enregistré' }); await load(); } finally { setSaving(false); } };
  const publish = async () => {
    if (!window.confirm('Publier ces textes ? Ils apparaîtront immédiatement sur le site public (sans redéploiement).')) return;
    setSaving(true);
    try { await adminTextsDraft(draft); const r = await adminTextsPublish(); toast({ title: 'Textes publiés', description: `${r.changed || 0} modification(s)` }); await load(); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm('Rétablir TOUS les textes par défaut ? Cette action est journalisée.')) return;
    setSaving(true);
    try { const r = await adminTextsReset(); setDraft({ ...r.published }); await load(); toast({ title: 'Textes réinitialisés' }); } finally { setSaving(false); }
  };

  if (!draft || !currentPage) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5" data-testid="public-texts-panel">
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-800 font-600">
        Modifiez les textes publics de chaque page. Aucun HTML autorisé (texte simple). Les modifications sont locales (Citytoon Engage) et n’altèrent jamais WordPress / Toocheke. Publier = visible immédiatement, sans redéploiement.
      </div>

      {/* Onglets pages */}
      <div className="flex flex-wrap gap-2">
        {schema.map((p) => (
          <button key={p.page} onClick={() => setPage(p.page)} data-testid={`page-tab-${p.page}`}
            className={`px-4 py-2 rounded-full text-sm font-800 transition-colors ${page === p.page ? 'bg-primary text-white' : 'bg-muted hover:bg-secondary/30'}`}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Édition */}
        <div className="space-y-5">
          {currentPage.blocks.map((b, bi) => (
            <div key={bi} className="bg-card border border-border rounded-2xl p-4">
              <h4 className="font-display font-700 text-base mb-3">{b.label}</h4>
              <div className="space-y-3">
                {b.fields.map((f) => <Field key={f.key} field={f} value={draft[f.key] ?? ''} onChange={upd} />)}
              </div>
            </div>
          ))}
        </div>

        {/* Aperçu */}
        <div>
          <div className="flex items-center gap-2 mb-2 sticky top-2">
            <span className="text-xs font-800 uppercase text-muted-foreground">Aperçu</span>
            <button onClick={() => setDevice('desktop')} data-testid="preview-desktop-btn" className={`w-8 h-8 rounded-full flex items-center justify-center ${device === 'desktop' ? 'bg-primary text-white' : 'bg-muted'}`}><Monitor className="w-4 h-4" /></button>
            <button onClick={() => setDevice('mobile')} data-testid="preview-mobile-btn" className={`w-8 h-8 rounded-full flex items-center justify-center ${device === 'mobile' ? 'bg-primary text-white' : 'bg-muted'}`}><Smartphone className="w-4 h-4" /></button>
            {dirty && <span className="ml-2 text-xs font-800 text-amber-600">• modifications non publiées</span>}
          </div>
          <Preview page={currentPage.page} draft={draft} device={device} />
        </div>
      </div>

      {/* Actions globales */}
      <div className="flex flex-wrap gap-2 pt-1 border-t border-border pt-4">
        <button onClick={saveDraft} disabled={saving} data-testid="text-save-draft-btn" className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted font-800 text-sm hover:bg-secondary/30 disabled:opacity-60"><Save className="w-4 h-4" /> Enregistrer le brouillon</button>
        <button onClick={publish} disabled={saving} data-testid="text-publish-btn" className="flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-white font-800 text-sm disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Publier</button>
        <button onClick={reset} disabled={saving} data-testid="text-reset-btn" className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted font-800 text-sm hover:bg-destructive hover:text-white disabled:opacity-60"><RotateCcw className="w-4 h-4" /> Rétablir les défauts</button>
      </div>

      {/* Historique */}
      <div className="bg-card border border-border rounded-2xl p-4">
        <button onClick={() => setShowLog((v) => !v)} data-testid="texts-log-toggle" className="flex items-center gap-2 font-800 text-sm w-full">
          <History className="w-4 h-4 text-primary" /> Historique des modifications ({log.length})
          <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showLog ? 'rotate-180' : ''}`} />
        </button>
        {showLog && (
          <div className="mt-3 space-y-3 max-h-80 overflow-y-auto">
            {log.length === 0 ? <p className="text-sm text-muted-foreground font-600">Aucune modification publiée pour l’instant.</p> : log.map((entry, i) => (
              <div key={i} className="border border-border rounded-xl p-3 text-sm">
                <p className="font-800 text-xs text-muted-foreground mb-1">{new Date(entry.at).toLocaleString('fr-FR')} — {entry.admin} — {entry.action} — {entry.changes?.length || 0} champ(s)</p>
                <ul className="space-y-1">
                  {(entry.changes || []).slice(0, 20).map((c, j) => (
                    <li key={j} className="text-xs"><span className="font-800">{c.key}</span> : <span className="text-red-600 line-through">{c.old || '∅'}</span> → <span className="text-green-700">{c.new || '∅'}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicTextsPanel;
